import type { QueryClient } from "@tanstack/react-query";

import { signInAnonymous } from "@/lib/sign-in-anonymous";
import {
  invalidateViewerContext,
  viewerContextQueryOptions,
  type GuestViewerContext,
  type MemberViewerContext,
} from "@/lib/viewer-context";

type AuthenticatedViewer = GuestViewerContext | MemberViewerContext;

let guestSessionPromise: Promise<AuthenticatedViewer> | null = null;

async function createGuestSession(queryClient: QueryClient): Promise<AuthenticatedViewer> {
  const result = await signInAnonymous();

  if (result.error) {
    throw new Error(result.error.message ?? "Unable to create a guest session");
  }

  await invalidateViewerContext(queryClient);
  const viewer = await queryClient.fetchQuery(viewerContextQueryOptions());

  if (!viewer.isAuthenticated) {
    throw new Error("Guest session was created but could not be resolved");
  }

  return viewer;
}

/**
 * Return the current authenticated viewer or lazily create one anonymous
 * Better Auth session. A module-level promise coalesces concurrent durable
 * actions into one `/sign-in/anonymous` request.
 */
export async function ensureGuestSession(queryClient: QueryClient): Promise<AuthenticatedViewer> {
  const viewer = await queryClient.fetchQuery(viewerContextQueryOptions());
  if (viewer.isAuthenticated) return viewer;

  guestSessionPromise ??= createGuestSession(queryClient).finally(() => {
    guestSessionPromise = null;
  });

  return guestSessionPromise;
}
